/**
 * A reconnect is indistinguishable from an arrival on the wire. The signaling
 * server puts a rejoining member through the whole join path, so everyone else
 * sees 'participantLeft' and then 'newParticipantArrived' for the same person
 * (Room#join -> leave -> notifyNewParticipant). Both messages have to keep
 * flowing, since the tile really does have to be torn down and rebuilt — but
 * the join sound must not fire, or one participant on unstable wifi chimes at
 * the whole meeting every few seconds.
 *
 * So a leave is remembered per member name, and an arrival for a name that just
 * left is read as that member coming back rather than as someone new.
 *
 * The member name is what makes this exact rather than a guess. It carries a
 * per-join UUID, minted only in joinMeeting and left alone by the reconnect
 * path — the server has to match on the same name to honour a reconnection
 * token. So a dropped connection returns under the name that left, while
 * someone who deliberately leaves and comes back has gone through a fresh join
 * and returns under a new one. The first is silent, the second still sounds,
 * which is the distinction a listener actually cares about.
 */
const leaveTimeByMemberName = new Map<string, number>();

/**
 * Wide enough for one reconnect: the supervisor takes up to its 2s monitor tick
 * to notice the drop, and joinVideoRoom allows the rejoin itself 10s before it
 * times out. A reconnect that burns several attempts outlives this and will
 * chime — by then the meeting is visibly degraded and already showing the
 * reconnect prompt, so a sound is no longer the surprising part.
 *
 * Deliberately generous rather than tight. Staying silent for a member who
 * genuinely left and came back inside the window costs one missed chime, and
 * they are on screen either way; chiming at every network blip is the thing
 * being fixed.
 */
export const RECONNECT_QUIET_PERIOD_MS = 15000;

export const rememberMemberLeft = (memberName: string) => {
    const now = Date.now();
    // Pruned on write rather than on a timer: a leave is the only thing that
    // grows this, and an entry stops meaning anything once its window closes.
    leaveTimeByMemberName.forEach((leaveTime, name) => {
        if (now - leaveTime > RECONNECT_QUIET_PERIOD_MS) {
            leaveTimeByMemberName.delete(name);
        }
    });
    leaveTimeByMemberName.set(memberName, now);
};

/**
 * Whether this arrival is a member returning from a drop rather than someone
 * new. An entry left behind by a member who really did leave is harmless: it
 * can only be consulted again if that same name arrives, which is precisely the
 * case it is meant to answer.
 */
export const memberIsReturningFromReconnect = (memberName: string) => {
    const leaveTime = leaveTimeByMemberName.get(memberName);
    return (
        leaveTime !== undefined &&
        Date.now() - leaveTime <= RECONNECT_QUIET_PERIOD_MS
    );
};
